import { useEffect, useState } from "react";
import { ArrowLeft, KeyRound, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function ForgotPassword({ initialEmail = "", onBack, onComplete }) {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState(initialEmail);
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (!cooldown) return;
    const id = setTimeout(() => setCooldown((v) => Math.max(0, v - 1)), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);
  const request = async () => {
    const { data } = await api.post("/auth/password-reset/request", { email });
    setChallenge(data.challenge_id); setStep("otp"); setCode(""); setCooldown(data.resend_after || 60);
  };
  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (step === "password" && password !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      if (step === "email") await request();
      else if (step === "otp") {
        const { data } = await api.post("/auth/password-reset/verify", { challenge_id: challenge, code });
        setToken(data.reset_token); setStep("password");
      } else {
        await api.post("/auth/password-reset/confirm", { reset_token: token, password, confirm_password: confirm });
        onComplete(email);
      }
    } catch (e) { setError(e.response ? formatApiError(e.response.data?.detail) : "Unable to connect. Please try again."); }
    finally { setBusy(false); }
  };
  return <div className="min-h-screen grain flex items-center justify-center px-6 py-10">
    <div className="w-full max-w-md">
      <button type="button" disabled={busy} onClick={onBack} className="flex items-center gap-2 text-sm text-slate-400 mb-8"><ArrowLeft size={16} />Back to sign in</button>
      <div className="w-12 h-12 rounded-2xl bg-emerald-400/15 flex items-center justify-center text-emerald-400 mb-4"><KeyRound size={24} /></div>
      <h1 className="font-display text-3xl font-bold mb-2">{step === "password" ? "Set a new password" : step === "otp" ? "Check your email" : "Forgot password?"}</h1>
      <p className="text-sm text-slate-400 mb-6">{step === "email" ? "Enter your registered email to receive a password reset code." : step === "otp" ? `If ${email} is registered, a six-digit code has been sent. It expires in 10 minutes.` : "Choose a new password. You’ll use it to sign in again."}</p>
      <form onSubmit={submit} className="space-y-4">
        {step === "email" && <Input aria-label="Registered email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Registered email" className="h-12 bg-[#121721] border-slate-700" />}
        {step === "otp" && <>
          <Input aria-label="Password reset code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code" className="h-12 bg-[#121721] border-slate-700" />
          <div className="flex justify-between text-xs">
            <button type="button" disabled={busy || cooldown > 0} onClick={async () => { setBusy(true); setError(""); try { await request(); } catch (e) { setError(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } }} className="text-emerald-400 disabled:text-slate-500">{cooldown ? `Resend in ${cooldown}s` : "Resend code"}</button>
            <button type="button" disabled={busy} onClick={() => { setStep("email"); setError(""); }} className="text-slate-400">Change email</button>
          </div>
        </>}
        {step === "password" && <>
          <Input aria-label="New password" type="password" autoComplete="new-password" minLength={8} maxLength={72} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" className="h-12 bg-[#121721] border-slate-700" />
          <Input aria-label="Confirm new password" type="password" autoComplete="new-password" minLength={8} maxLength={72} required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" className="h-12 bg-[#121721] border-slate-700" />
          <p className="text-xs text-slate-500">At least 8 characters. Existing sessions will be signed out.</p>
        </>}
        {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
        <Button type="submit" disabled={busy} className="w-full h-12 bg-emerald-400 hover:bg-emerald-500 text-black font-semibold">{busy ? <Loader2 className="animate-spin" size={18} /> : step === "email" ? "Send reset code" : step === "otp" ? "Verify code" : "Update password"}</Button>
      </form>
    </div>
  </div>;
}
